import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@backbase/shared/feature/config';
import { SafeLogger } from '@backbase/shared/util/app-core';
import CryptoJS from 'crypto-js';

@Injectable({
    providedIn: 'root'
})
export class LoansSharedDataService {
    private readonly secretKey: string;
    private readonly loanRouteSegments = [
        'car-loan-application',
        'credit-card-application',
        'home-loan-application',
        'loan-application',
        'top-up-loan',
        'repay-loan'
    ];

    constructor(
        private router: Router,
        @Inject(ENVIRONMENT_CONFIG) environmentConfig: EnvironmentConfig,
        private readonly logger: SafeLogger,
    ) {
        this.secretKey = environmentConfig.localStorageEncryptionKey;
    }

    /**
     * Encrypt loan data for URL transmission
     * @param data Loan data to encrypt
     * @returns URL-encoded encrypted string
     */
    encryptLoanDataForUrl(data: any): string {
        const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), this.secretKey).toString();
        return encodeURIComponent(encrypted);
    }

    /**
     * Decrypt loan data from URL parameter
     * @param encryptedData Encrypted loan data from route
     * @returns Decrypted loan data
     */
    decryptLoanDataFromUrl(encryptedData: string): any {
        try {
            // Always decode once - this matches the pattern from AccountDecryptionService
            // The data goes through: encode (us) → encode (Angular router) → decode (Angular paramMap) → decode (here)
            const decoded = decodeURIComponent(encryptedData);
            const decryptedBytes = CryptoJS.AES.decrypt(decoded, this.secretKey);
            const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);
            return JSON.parse(decryptedString);
        } catch (error) {
            this.logger.error('Error decrypting loan data from URL', error);
            return null;
        }
    }

    /**
     * Get encrypted loan data from route parameters and decrypt it
     * @param route ActivatedRoute to extract params from
     * @param currentUrl Current URL for fallback extraction
     * @returns Decrypted loan data object
     */
    getLoanDataFromRoute(route: ActivatedRoute, currentUrl: string): any {
        let encryptedData = this.getEncryptedLoanDataFromRouteParams(route);

        // Fallback: extract from URL segments and decode manually
        if (!encryptedData) {
            encryptedData = this.extractEncryptedLoanDataFromUrl(currentUrl, undefined, true);
        }

        if (!encryptedData) {
            return null;
        }

        return this.decryptLoanDataFromUrl(encryptedData);
    }

    /**
     * @deprecated Use getLoanDataFromRoute() instead
     * Get encrypted loan data from route parameters
     * @param route ActivatedRoute to extract params from
     * @param currentUrl Current URL for fallback extraction
     * @returns Encrypted loan data string from URL
     */
    getEncryptedLoanDataFromRoute(route: ActivatedRoute, currentUrl: string): string {
        let encryptedData = this.getEncryptedLoanDataFromRouteParams(route);

        // Fallback: extract from URL segments
        if (!encryptedData) {
            encryptedData = this.extractEncryptedLoanDataFromUrl(currentUrl);
        }

        return encryptedData || '';
    }

    /**
     * @deprecated Use encryptLoanDataForUrl and navigate with encrypted data in URL instead
     * Encrypt the loan info and store in localstorage
     */
    storeLoanToApplyData(data: any) {
        this.clearAllLoanData();
        const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(data), this.secretKey).toString();
        localStorage.setItem('loan_application_info', encryptedData);
    }

    /**
     * @deprecated Use decryptLoanDataFromUrl with route params instead
     * Decrypt the loan info from localstorage using the secret
     */
    getLoanToApplyData() {
        const storedLoanData = localStorage.getItem('loan_application_info') || null;

        if (storedLoanData === null) {
            this.router.navigate(['/loans/get-a-loan'])
        } else {
            const bytes = CryptoJS.AES.decrypt(storedLoanData, this.secretKey);
            const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            return decryptedData;
        }
        return null;
    }

    removeLoanToApplyData() {
        localStorage.removeItem('loan_application_info')
    }

    /**
     * @deprecated Use encryptLoanDataForUrl and navigate with encrypted data in URL instead
     */
    storeLoanToRepayData(data: any) {
        this.clearAllLoanData();
        const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(data), this.secretKey).toString();
        localStorage.setItem('loan_repayment_info', encryptedData);
    }

    /**
     * @deprecated Use decryptLoanDataFromUrl with route params instead
     */
    getLoanToRepayData() {
        const storedLoanData = localStorage.getItem('loan_repayment_info') || null;

        if (storedLoanData === null) {
            this.router.navigate(['/']);
        } else {
            const bytes = CryptoJS.AES.decrypt(storedLoanData, this.secretKey);
            const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            return decryptedData;
        }
        return null
    }

    removeLoanToRepayData() {
        localStorage.removeItem('loan_repayment_info');
    }

    /**
     * @deprecated Use encryptLoanDataForUrl and navigate with encrypted data in URL instead
     */
    storeLoanTopUpData(data: any) {
        this.clearAllLoanData();
        const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(data), this.secretKey).toString();
        localStorage.setItem('loan_top_up_info', encryptedData);
    }

    /**
     * @deprecated Use decryptLoanDataFromUrl with route params instead
     */
    getLoanTopUpData() {
        const storedLoanData = localStorage.getItem('loan_top_up_info') || null;

        if (storedLoanData === null) {
            this.router.navigate(['/']);
        } else {
            const bytes = CryptoJS.AES.decrypt(storedLoanData, this.secretKey);
            const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            return decryptedData;
        }
        return null
    }

    removeLoanTopUpData() {
        localStorage.removeItem('loan_top_up_info');
    }

    clearAllLoanData() {
        this.removeLoanToApplyData();
        this.removeLoanToRepayData();
        this.removeLoanTopUpData();
    }

    extractEncryptedLoanDataFromUrl(currentUrl: string, routeSegment?: string, doubleDecode = false): string | null {
        const urlSegments = currentUrl.split('/');
        for (let i = 0; i < urlSegments.length; i++) {
            const previousSegment = i > 0 ? urlSegments[i - 1] : '';
            const isLoanRoute = routeSegment
                ? previousSegment === routeSegment
                : this.loanRouteSegments.includes(previousSegment);

            if (isLoanRoute) {
                let raw = urlSegments[i];
                if (doubleDecode) {
                    try {
                        raw = decodeURIComponent(raw);
                        raw = decodeURIComponent(raw);
                    } catch (e) {
                        this.logger.warn('Double decode failed', e);
                    }
                }
                return raw;
            }
        }
        return null;
    }

    private getEncryptedLoanDataFromRouteParams(route: ActivatedRoute): string | null {
        let encryptedData: string | null = null;
        let currentRoute: ActivatedRoute | null = route;

        while (currentRoute && !encryptedData) {
            encryptedData = currentRoute.snapshot.paramMap.get('loanData');
            currentRoute = currentRoute.parent;
        }

        return encryptedData;
    }
}
